// Shared compact sort control — one small dropdown (sort key) + a direction
// toggle button (asc/desc). Used by My Contacts and Subscribers so both look and
// behave identically: a small control in the top-right, never a full-width block.
import { h } from '../components/dom.js';

export type SortDir = 'asc' | 'desc';

export interface SortOption {
  value: string;
  // Base label WITHOUT direction, e.g. "Name" or "Subscribed".
  label: string;
  // Default direction when this key is first selected (e.g. name -> asc,
  // recency -> desc). Users can still flip it with the toggle.
  defaultDir?: SortDir;
}

export interface SortControl {
  el: HTMLElement;
  key: () => string;
  dir: () => SortDir;
}

// buildSortControl renders "[Sort: <key> ▾] [↑/↓]" and calls onChange whenever
// either the key or the direction changes.
export function buildSortControl(options: SortOption[], onChange: () => void): SortControl {
  let key = options[0].value;
  let dir: SortDir = options[0].defaultDir || 'asc';

  const sel = h('select', { class: 'input btn-sm', 'aria-label': 'Sort by', style: 'width:auto' },
    ...options.map((o) => h('option', { value: o.value }, 'Sort: ' + o.label))) as HTMLSelectElement;
  sel.value = key;

  const dirBtn = h('button', { class: 'btn btn-ghost btn-sm', type: 'button', 'aria-label': 'Toggle sort direction', title: 'Toggle ascending / descending' }) as HTMLButtonElement;
  const paintDir = (): void => { dirBtn.textContent = dir === 'asc' ? '↑ Asc' : '↓ Desc'; };
  paintDir();

  sel.addEventListener('change', () => {
    key = sel.value;
    // Reset to the new key's natural default direction when switching keys.
    dir = (options.find((o) => o.value === key)?.defaultDir) || 'asc';
    paintDir();
    onChange();
  });
  dirBtn.addEventListener('click', () => {
    dir = dir === 'asc' ? 'desc' : 'asc';
    paintDir();
    onChange();
  });

  const el = h('div', { class: 'row', style: 'gap:6px;align-items:center' }, sel, dirBtn);
  return { el, key: () => key, dir: () => dir };
}
